import { defineAsyncComponent, defineComponent, ref } from 'vue';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { StandardAlertsPlugin } from 'src/plugins/alert/alertPlugin';
export default defineComponent({
    components: {
        chatInput: defineAsyncComponent(() => import(
        /* webpackChunkName: "Chat" */
        'src/features/messaging/chat/components/input/chatInput.vue')),
        generalDialog: defineAsyncComponent(() => import(
        /* webpackChunkName: "Dialog" */
        'components/general/dialog.vue')),
    },
    emits: ['upload'],
    setup(props, context) {
        const { errorAlert } = StandardAlertsPlugin();
        const confirmShow = ref(false);
        const appropriateFileTypes = {
            'img': [
                'image/jpeg',
                'image/gif',
                'image/png'
            ],
            'file': [
                'application/pdf',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'application/vnd.ms-powerpoint',
                'application/vnd.ms-excel',
                'application/msword',
                'image/jpeg',
                'image/gif',
                'image/png'
            ]
        };
        /**
         * Parse file
         */
        const fileToUpload = ref(null);
        const filePreview = ref(null);
        const uploadType = ref(null);
        /**
         * Before file upload, check if the file type is supported
         * and preview for users to confirm upload
         * @param fileData file
         * @param type type of file
         */
        function beforeUpload(fileData, type) {
            /**
             * Check if the type matches with the appropriate
             * file types allowed
             */
            if (appropriateFileTypes[type].indexOf(fileData.type) === -1) {
                errorAlert('File type not supported');
                return;
            }
            fileToUpload.value = fileData;
            // preview
            filePreview.value = URL.createObjectURL(fileData);
            uploadType.value = type === 'file'
                ? 'attachment'
                : 'image';
            confirmShow.value = true;
        }
        /**
         * Clear data and close dialog
         */
        function clearData(dialogOpened = false) {
            if (dialogOpened) {
                confirmShow.value = true;
                return;
            }
            fileToUpload.value = null;
            filePreview.value = null;
            uploadType.value = null;
            confirmShow.value = false;
        }
        /**
         * Load image dimentsions
         */
        const { subject: debouncedImgLoad$ } = rxInitSubjectCallback();
        const previewDimensions = ref({});
        const previewPicRef = ref(null);
        useObservable(debouncedImgLoad$.pipe(debounceTime(800), filter(() => uploadType.value === 'image'), tap(() => {
            const width = previewPicRef.value?.naturalWidth || 0;
            const height = previewPicRef.value?.naturalHeight || 0;
            const ratio = width / height;
            previewDimensions.value = {
                width,
                height,
                ratio: parseFloat(ratio.toPrecision(2))
            };
        })));
        /**
         * Get dimensions of the image
         */
        function getDimensions() {
            debouncedImgLoad$.next('');
        }
        /**
         * Upload
         */
        function uploadPics(picTxt) {
            context.emit('upload', {
                fileUpload: fileToUpload.value,
                previewDimensions: previewDimensions.value,
                text: picTxt,
                uploadType: uploadType.value
            });
        }
        return {
            confirmShow,
            filePreview,
            uploadType,
            beforeUpload,
            clearData,
            previewPicRef,
            getDimensions,
            uploadPics
        };
    }
});
